<template>
    <div>
        <div id="report69">
            <report-header
                :title="title"
                :isShowDateRange="true"
                :formatDateRangeTitle="dateRange"
            />
            <b-card id="report-card">
                <div id="report-body" class="w-100">
                    <div
                        id="report-body"
                        class="w-100"
                        v-if="providers.length > 0"
                    >
                        <div
                            class="w-100 sales-provider-summary-table"
                            v-for="provider in providers"
                            :key="provider.id"
                        >
                            <p class="item-name">
                                {{ provider.provider_name }}
                            </p>
                            <QTable
                                :columns="formattedColumns"
                                :loading="isLoading"
                                :data="details[provider.provider_name]"
                                :rows-per-page-options="[0]"
                                :pagination.sync="pagination"
                                hide-bottom
                                class="q-table"
                            >
                                <template v-slot:body-cell-type="{ row }">
                                    <td style="width: 160px;">
                                        <b v-if="row.type === 'Total'">
                                            {{ row.type }}
                                        </b>
                                        <span v-else>
                                            {{ row.type }}
                                        </span>
                                    </td>
                                </template>
                                <template
                                    v-slot:body-cell-gross_excl_tax="{
                                        col,
                                        row
                                    }"
                                >
                                    <td>
                                        <b v-if="row.type === 'Total'">
                                            {{ getValue(row[col.field]) }}
                                        </b>
                                        <span v-else>
                                            {{ getValue(row[col.field]) }}
                                        </span>
                                    </td>
                                </template>
                                <template
                                    v-slot:body-cell-gross_percent="{
                                        col,
                                        row
                                    }"
                                >
                                    <td>
                                        <b v-if="row.type === 'Total'">
                                            {{ row[col.field] }}
                                        </b>
                                        <span v-else>
                                            {{ row[col.field] }}
                                        </span>
                                    </td>
                                </template>
                                <template
                                    v-slot:body-cell-total_percent="{
                                        col,
                                        row
                                    }"
                                >
                                    <td>
                                        <b v-if="row.type === 'Total'">
                                            {{ row[col.field] }}
                                        </b>
                                        <span v-else>
                                            {{ row[col.field] }}
                                        </span>
                                    </td>
                                </template>
                            </QTable>
                        </div>
                        <QTable
                            id="grand-total"
                            :columns="formattedColumns"
                            :loading="isLoading"
                            :data="grandTotalData"
                            hide-bottom
                            class="q-table mb-4"
                        />
                    </div>
                    <div v-else-if="isFirstLoad" class="w-100 pt-2 pb-2">
                        <i
                            >Please select the
                            <a @click="openFilterModal" class="text-link"
                                >filter</a
                            >
                        </i>
                    </div>
                    <div v-else class="w-100 pt-2 pb-2">
                        <div class="w-100">
                            <QTable
                                id="no-records"
                                :columns="formattedColumns"
                                :loading="isLoading"
                                :data="[]"
                                hide-bottom
                                class="q-table border-top"
                            />
                        </div>
                        <div class="p-2">No records available</div>
                    </div>
                </div>
            </b-card>
        </div>
    </div>
</template>

<script>
import { QTable } from "quasar";
import { forEach, isEmpty } from "lodash";
import CREPORT from "@/constants/report";

import Report from "@/mixins/report";
import ReportService from "@/services/report";

import "@/css/quasar-table.css";

export default {
    mixins: [Report],
    components: {
        QTable,
        ReportHeader: () => import("@/components/report/report-header")
    },
    props: {
        data: {
            type: Object,
            default() {
                return {
                    start: this.getStartDate("isoWeek"),
                    end: this.getEndDate("day")
                };
            }
        },
        dialogs: {
            type: Object,
            default() {
                return {};
            }
        }
    },
    data() {
        return {
            isLoading: false,
            isError: false,
            isFirstLoad: true,
            locale: "en-US",
            result: [],
            dateRange: "",
            refresh: 0,
            title: "Sales By Provider (Summary)",
            resource: null,
            providers: [],
            details: [],
            currency: "",
            eachTotal: null,
            grandTotalData: [],
            pagination: {
                page: 1,
                rowsPerPage: 0 // 0 means all rows
            }
        };
    },
    mounted() {
        let self = this;
        // --- toggle navigation options
        self.data.isShowDateRange = true;
        self.data.reportId = CREPORT.TYPE.SALES_BY_PROVIDER_SUMMARY;

        self._getSetting().then(() => {
            self.initSetDates().then(() => {
                self.openFilterModal();
            });
        });

        self.$root.$on("report-filter-date-range:refresh", this.filterReport);
        self.$root.$on(
            "sales-provider-summary:refresh",
            this.refreshSalesProviderSummary
        );

        window.scrollTo(0, 0);
    },
    beforeDestroy: function () {
        this.data.isShowDateRange = false;
        this.data.reportId = null;

        this.$root.$off("report-filter-date-range:refresh", this.filterReport);
        this.$root.$off("sales-provider-summary:refresh");
    },
    computed: {
        formattedColumns() {
            return [
                {
                    name: "type",
                    label: "Type",
                    field: row => row.type,
                    format: val => {
                        return val ? val : "-";
                    },
                    align: "left",
                    style: "width: 160px;"
                },
                {
                    name: "category",
                    label: "Category",
                    field: "category",
                    align: "left",
                    style: "width: 200px;"
                },
                {
                    name: "patients",
                    label: "Patients",
                    field: "unique_patient_count",
                    align: "left"
                },
                {
                    name: "invoices",
                    label: "Invoices",
                    field: "unique_invoice_count",
                    align: "left"
                },
                {
                    name: "qty_sold",
                    label: "Qty Sold",
                    field: "qty_sold",
                    align: "left"
                },
                {
                    name: "gross_excl_tax",
                    label: "Gross (excl. Tax) (" + this.currency + ")",
                    field: "gross_excl_tax",
                    align: "left"
                },
                {
                    name: "gross_percent",
                    label: "% Of Gross (excl. Tax)",
                    field: "%_of_gross_excl_tax",
                    align: "left"
                },
                {
                    name: "total_percent",
                    label: "% Of Total",
                    field: "%_of_total",
                    align: "left"
                }
            ];
        }
    },
    methods: {
        filterReport(params) {
            params.refresh = 0;
            this.init(params);
        },
        refreshSalesProviderSummary() {
            this.refresh = 1;
            this.init();
        },
        async init(params) {
            let self = this;
            self.isLoading = true;
            self.currency = this._currency;

            if (params) {
                self.data.from = params.from;
                self.data.to = params.to;
                self.refresh = params.refresh;
            }
            self.dateRange = self.getDateRange(self.data.from, self.data.to);

            ReportService.getReport(CREPORT.TYPE.SALES_BY_PROVIDER_SUMMARY, {
                from: self.data.from,
                to: self.data.to,
                refresh: self.refresh
            })
                .then(response => {
                    if (
                        response &&
                        response.message &&
                        response.message.code === 200
                    ) {
                        const resource = response.resource;
                        self.resource = response.resource;

                        self.details = self.processDetails(
                            resource.results.detail_data
                        );

                        self.dateRange = self.getDateRange(
                            resource.start_date,
                            resource.end_date
                        );
                        self.dynamicPageTitle(self.title);

                        this.getGrandTotalData(resource.results.grand_total);
                    } else if (response && response.queue) {
                        let dateTo = self.data.to;
                        let dateFrom = self.data.from;
                        let query = self.$route.query;
                        self._message(response.message, "warning");
                        if (
                            !(
                                query &&
                                query.to == dateTo &&
                                query.from == dateFrom
                            )
                        ) {
                            self.$router.replace({
                                query: { to: dateTo, from: dateFrom }
                            });
                        }
                    } else {
                        this._message(
                            "Error: Unable to get the report",
                            "danger"
                        );
                    }
                })
                .catch(e => {
                    this._message("Error: Unable to get the report", "danger");
                    console.error(e);
                })
                .finally(() => {
                    self.isFirstLoad = false;
                    self.isLoading = false;
                });
        },
        async initSetDates() {
            let query = this.$route.query;
            if (!isEmpty(query.to) && !isEmpty(query.from)) {
                this.data.datePickerOptionDefault = 1;
                this.data.start = moment.unix(query.from).tz(this._timeZone);
                this.data.end = moment.unix(query.to).tz(this._timeZone);
                this.data.from = query.from;
                this.data.to = query.to;
                this.dateRange = this.getDateRange(query.from, query.to);
            } else {
                this.data.start = this.getStartDate("isoWeek");
                this.data.end = this.getEndDate("day");
                this.data.from = this.converToUnix(this.data.start);
                this.data.to = this.converToUnix(this.data.end);
            }
        },
        getGrandTotalData(total) {
            this.grandTotalData = [
                {
                    type: "Grand Total (Unique)",
                    qty_sold: total.qty_sold,
                    gross_excl_tax: total.gross_excl_tax,
                    "%_of_gross_excl_tax": total["%_of_gross_excl_tax"],
                    "%_of_total": total["%_of_total"]
                }
            ];
        },
        processDetails(details) {
            let self = this;
            self.providers = [];

            if (!isEmpty(details)) {
                forEach(details, function (val, key) {
                    let thisTotal = self.resource.results.each_total[key];
                    let grossTotal = thisTotal.gross_excl_tax;
                    let grossPercent = thisTotal["%_of_gross_excl_tax"];
                    let totalPercent = thisTotal["%_of_total"];

                    self.providers.push({
                        provider_name: key,
                        id: "id-" + key
                    });

                    val.push({
                        type: "Total",
                        gross_excl_tax: grossTotal,
                        "%_of_gross_excl_tax": grossPercent,
                        "%_of_total": totalPercent
                    });
                });
            }

            return details;
        },
        getValue(value) {
            return value == 0 || value == 0.0 ? "(" + value + ")" : value;
        }
    }
};
</script>
<style type="text/css">
#grand-total.q-table td {
    font-weight: bold !important;
    border-top: 1px solid rgba(0, 0, 0, 0.12) !important;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important;
}

#grand-total.q-table th {
    visibility: hidden;
}

#no-records.q-table {
    border-top: 0 !important;
}

#report-card .sales-provider-summary-table table tr th {
    white-space: normal;
}
</style>
